// Enhanced Form Validation System
// Converted from frontend/lib/form-validation.js

interface ValidationRule {
  test: (value: string) => boolean | ValidationResult;
  message: string;
  strength?: 'weak' | 'medium' | 'strong';
}

interface ValidationResult {
  isValid: boolean;
  strength?: string;
  checks?: Record<string, boolean>;
  score?: number;
}

interface FormField {
  element: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
  rules: string[];
  required: boolean;
  helpText?: string;
}

export class FormValidation {
  private validators: Map<string, ValidationRule> = new Map();
  private forms: Map<string, Map<string, FormField>> = new Map();
  private debounceTimers: Map<string, NodeJS.Timeout> = new Map();
  private validationMessages: Map<string, string> = new Map();
  private helpTexts: Map<string, string> = new Map();

  constructor() {
    this.init();
  }

  private init(): void {
    this.setupDefaultValidators();
    this.setupDefaultMessages();
    this.setupFormObserver();
  }

  // Setup default validation rules
  private setupDefaultValidators(): void {
    // Email validation
    this.validators.set('email', {
      test: (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
      message: 'Please enter a valid email address',
      strength: 'medium'
    });

    // Password strength validation
    this.validators.set('password', {
      test: (value: string) => {
        const hasLength = value.length >= 8;
        const hasUpper = /[A-Z]/.test(value);
        const hasLower = /[a-z]/.test(value);
        const hasNumber = /\d/.test(value);
        const hasSpecial = /[!@#$%^&*(),.?\":{}|<>]/.test(value);
        
        return {
          isValid: hasLength && hasUpper && hasLower && hasNumber,
          strength: this.calculatePasswordStrength(value),
          checks: {
            hasLength,
            hasUpper,
            hasLower,
            hasNumber,
            hasSpecial
          },
          score: [hasLength, hasUpper, hasLower, hasNumber, hasSpecial].filter(Boolean).length
        };
      },
      message: 'Password must be at least 8 characters with uppercase, lowercase, and number',
      strength: 'strong'
    });

    // Required field validation
    this.validators.set('required', {
      test: (value: string) => value.trim().length > 0,
      message: 'This field is required',
      strength: 'weak'
    });

    // Minimum length validation
    this.validators.set('minLength', {
      test: (value: string, minLength = 3) => value.length >= minLength,
      message: 'Field is too short',
      strength: 'weak'
    });

    // Maximum length validation
    this.validators.set('maxLength', {
      test: (value: string, maxLength = 255) => value.length <= maxLength,
      message: 'Field is too long',
      strength: 'weak'
    });

    // Phone number validation
    this.validators.set('phone', {
      test: (value: string) => /^[\+]?[\d\s\-\(\)]{10,}$/.test(value),
      message: 'Please enter a valid phone number',
      strength: 'medium'
    });

    // URL validation
    this.validators.set('url', {
      test: (value: string) => {
        try {
          new URL(value);
          return true;
        } catch {
          return false;
        }
      },
      message: 'Please enter a valid URL',
      strength: 'medium'
    });

    // Numeric validation
    this.validators.set('numeric', {
      test: (value: string) => /^\d+$/.test(value),
      message: 'Please enter only numbers',
      strength: 'weak'
    });

    // Date validation
    this.validators.set('date', {
      test: (value: string) => {
        const date = new Date(value);
        return !isNaN(date.getTime());
      },
      message: 'Please enter a valid date',
      strength: 'medium'
    });
  }

  private setupDefaultMessages(): void {
    this.validationMessages.set('email', 'Enter a valid email address');
    this.validationMessages.set('password', 'Password requirements not met');
    this.validationMessages.set('required', 'This field is required');
    this.validationMessages.set('minLength', 'Too short');
    this.validationMessages.set('maxLength', 'Too long');
    this.validationMessages.set('phone', 'Enter a valid phone number');
    this.validationMessages.set('url', 'Enter a valid URL');
    this.validationMessages.set('numeric', 'Numbers only');
    this.validationMessages.set('date', 'Enter a valid date');
  }

  private setupFormObserver(): void {
    if (typeof window === 'undefined') return;

    // Use MutationObserver to watch for new forms
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            const forms = element.querySelectorAll('form[data-validate]');
            forms.forEach((form) => this.initializeForm(form as HTMLFormElement));
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Initialize existing forms
    document.addEventListener('DOMContentLoaded', () => {
      const forms = document.querySelectorAll('form[data-validate]');
      forms.forEach((form) => this.initializeForm(form as HTMLFormElement));
    });
  }

  // Initialize a form for validation
  initializeForm(form: HTMLFormElement): void {
    const formId = form.id || `form_${Date.now()}`;
    const formFields = new Map<string, FormField>();

    // Find all form fields with validation rules
    const fields = form.querySelectorAll('[data-validate]');
    
    fields.forEach((fieldElement) => {
      const field = fieldElement as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      const rules = field.dataset.validate?.split(',').map(rule => rule.trim()) || [];
      const required = field.hasAttribute('required') || rules.includes('required');
      const helpText = field.dataset.help;

      const fieldInfo: FormField = {
        element: field,
        rules,
        required,
        helpText
      };

      formFields.set(field.name || field.id, fieldInfo);

      // Add event listeners
      this.attachFieldListeners(field, fieldInfo);
    });

    this.forms.set(formId, formFields);

    // Add form submit listener
    form.addEventListener('submit', (e) => this.handleSubmit(e, formId));
  }

  private attachFieldListeners(field: HTMLElement, fieldInfo: FormField): void {
    const input = field as HTMLInputElement;
    
    // Real-time validation on input
    input.addEventListener('input', () => {
      this.debounceValidation(input, fieldInfo, 300);
    });

    // Validate on blur
    input.addEventListener('blur', () => {
      this.validateField(input, fieldInfo);
    });

    // Focus handling
    input.addEventListener('focus', () => {
      this.showHelpText(input, fieldInfo);
    });
  }

  private debounceValidation(field: HTMLInputElement, fieldInfo: FormField, delay: number): void {
    const key = field.name || field.id;
    
    if (this.debounceTimers.has(key)) {
      clearTimeout(this.debounceTimers.get(key)!);
    }

    const timer = setTimeout(() => {
      this.validateField(field, fieldInfo);
    }, delay);

    this.debounceTimers.set(key, timer);
  }

  // Validate a single field
  validateField(field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, fieldInfo: FormField): boolean {
    const value = field.value;
    let isValid = true;
    let firstError = '';

    // Clear previous validation state
    this.clearFieldValidation(field);

    // Check each validation rule
    for (const ruleName of fieldInfo.rules) {
      const validator = this.validators.get(ruleName);
      if (!validator) continue;

      const result = validator.test(value);
      const ruleValid = typeof result === 'boolean' ? result : result.isValid;

      if (!ruleValid) {
        isValid = false;
        if (!firstError) {
          firstError = validator.message;
        }
        this.showFieldError(field, validator.message);
        break;
      } else if (typeof result === 'object' && result.checks) {
        // Handle password strength display
        this.showPasswordStrength(field, result);
      }
    }

    // Update field state
    if (isValid) {
      this.showFieldSuccess(field);
    }

    return isValid;
  }

  private clearFieldValidation(field: HTMLElement): void {
    field.classList.remove('invalid', 'valid', 'error');
    
    // Remove error messages
    const errorElement = field.parentElement?.querySelector('.field-error');
    if (errorElement) {
      errorElement.remove();
    }

    // Remove strength indicator
    const strengthElement = field.parentElement?.querySelector('.password-strength');
    if (strengthElement) {
      strengthElement.remove();
    }
  }

  private showFieldError(field: HTMLElement, message: string): void {
    field.classList.add('invalid', 'error');
    field.setAttribute('aria-invalid', 'true');

    // Create error message element
    const errorElement = document.createElement('div');
    errorElement.className = 'field-error text-red-600 text-sm mt-1';
    errorElement.textContent = message;
    errorElement.setAttribute('role', 'alert');

    field.parentElement?.appendChild(errorElement);
  }

  private showFieldSuccess(field: HTMLElement): void {
    field.classList.add('valid');
    field.classList.remove('invalid', 'error');
    field.setAttribute('aria-invalid', 'false');
  }

  private showHelpText(field: HTMLElement, fieldInfo: FormField): void {
    if (!fieldInfo.helpText) return;

    // Remove existing help text
    const existingHelp = field.parentElement?.querySelector('.field-help');
    if (existingHelp) {
      existingHelp.remove();
    }

    const helpElement = document.createElement('div');
    helpElement.className = 'field-help text-gray-600 text-sm mt-1';
    helpElement.textContent = fieldInfo.helpText;

    field.parentElement?.appendChild(helpElement);
  }

  private showPasswordStrength(field: HTMLElement, result: ValidationResult): void {
    if (!result.checks || !result.score) return;

    // Remove existing strength indicator
    const existingStrength = field.parentElement?.querySelector('.password-strength');
    if (existingStrength) {
      existingStrength.remove();
    }

    const strengthElement = document.createElement('div');
    strengthElement.className = 'password-strength mt-2';

    const strengthBar = document.createElement('div');
    strengthBar.className = 'strength-bar bg-gray-200 rounded-full h-2 mb-2';

    const strengthFill = document.createElement('div');
    const percentage = (result.score / 5) * 100;
    strengthFill.className = `strength-fill h-full rounded-full transition-all duration-300 ${
      percentage < 40 ? 'bg-red-500' : percentage < 80 ? 'bg-yellow-500' : 'bg-green-500'
    }`;
    strengthFill.style.width = `${percentage}%`;

    strengthBar.appendChild(strengthFill);
    strengthElement.appendChild(strengthBar);

    // Add requirement checklist
    const checkList = document.createElement('ul');
    checkList.className = 'text-xs space-y-1';

    const requirements = [
      { key: 'hasLength', text: 'At least 8 characters' },
      { key: 'hasUpper', text: 'Uppercase letter' },
      { key: 'hasLower', text: 'Lowercase letter' },
      { key: 'hasNumber', text: 'Number' },
      { key: 'hasSpecial', text: 'Special character' }
    ];

    requirements.forEach(req => {
      const li = document.createElement('li');
      const isValid = result.checks![req.key];
      li.className = isValid ? 'text-green-600' : 'text-gray-400';
      li.innerHTML = `${isValid ? '✓' : '○'} ${req.text}`;
      checkList.appendChild(li);
    });

    strengthElement.appendChild(checkList);
    field.parentElement?.appendChild(strengthElement);
  }

  private calculatePasswordStrength(password: string): string {
    const checks = [
      password.length >= 8,
      /[A-Z]/.test(password),
      /[a-z]/.test(password),
      /\d/.test(password),
      /[!@#$%^&*(),.?\":{}|<>]/.test(password)
    ];

    const score = checks.filter(Boolean).length;

    if (score < 2) return 'very-weak';
    if (score < 3) return 'weak';
    if (score < 4) return 'medium';
    if (score < 5) return 'strong';
    return 'very-strong';
  }

  // Validate entire form
  validateForm(formId: string): boolean {
    const formFields = this.forms.get(formId);
    if (!formFields) return false;

    let isFormValid = true;

    formFields.forEach((fieldInfo, fieldName) => {
      const isFieldValid = this.validateField(fieldInfo.element, fieldInfo);
      if (!isFieldValid) {
        isFormValid = false;
      }
    });

    return isFormValid;
  }

  private handleSubmit(event: Event, formId: string): void {
    const isValid = this.validateForm(formId);
    
    if (!isValid) {
      event.preventDefault();
      
      // Focus first invalid field
      const formFields = this.forms.get(formId);
      if (formFields) {
        for (const [, fieldInfo] of formFields) {
          if (fieldInfo.element.classList.contains('invalid')) {
            fieldInfo.element.focus();
            break;
          }
        }
      }
    }
  }

  // Add custom validator
  addValidator(name: string, rule: ValidationRule): void {
    this.validators.set(name, rule);
  }

  // Remove validator
  removeValidator(name: string): void {
    this.validators.delete(name);
  }

  // Get form validity
  isFormValid(formId: string): boolean {
    return this.validateForm(formId);
  }
}

// Export singleton instance
export const formValidation = new FormValidation();